import React, { useMemo, useState } from 'react';
import Navbar from '../NavBar';
import Sidebar from './Sidebar';
import ProductGrid from './ProductGrid';
import Footer from '../Footer';
import SectionBreak from '../SectionBreak';
import { fetchProducts, getFallbackProducts } from './productService';

const PAGE_SIZE = 9;

const toFiniteNumber = (value) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const getPriceBounds = (product = {}) => {
    const fallbackPrice = toFiniteNumber(product.price) ?? 0;
    const min = toFiniteNumber(product.minPrice) ?? fallbackPrice;
    const rawMax = toFiniteNumber(product.maxPrice);
    const max = rawMax !== null && rawMax > 0 ? rawMax : Math.max(min, fallbackPrice);
    return {
        min: min ?? 0,
        max: max ?? min ?? 0,
    };
};

const Index = () => {
    const fallbackProducts = useMemo(() => getFallbackProducts(), []);

    const [products, setProducts] = useState(fallbackProducts);
    const [filteredProducts, setFilteredProducts] = useState(fallbackProducts);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [priceRange, setPriceRange] = useState({ min: '', max: '' });
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const parsedMin = React.useMemo(() => {
        if (priceRange.min === '') {
            return null;
        }
        const cleaned = priceRange.min.replace(',', '.');
        const value = parseFloat(cleaned);
        return Number.isFinite(value) ? value : null;
    }, [priceRange.min]);

    const parsedMax = React.useMemo(() => {
        if (priceRange.max === '') {
            return null;
        }
        const cleaned = priceRange.max.replace(',', '.');
        const value = parseFloat(cleaned);
        return Number.isFinite(value) ? value : null;
    }, [priceRange.max]);

    const handleFilterChange = (type, value, isChecked) => {
        if (type === 'category') {
            setSelectedCategories(prevCategories => {
                if (isChecked) {
                    return [...prevCategories, value];
                } else {
                    return prevCategories.filter(category => category !== value);
                }
            });
        } else if (type === 'brand') {
            setSelectedBrands(prevBrands => {
                if (isChecked) {
                    return [...prevBrands, value];
                } else {
                    return prevBrands.filter(brand => brand !== value);
                }
            });
        }
    };

    const handlePriceChange = (field, rawValue) => {
        setPriceRange(prev => ({
            ...prev,
            [field]: rawValue,
        }));
    };

    React.useEffect(() => {
        let isMounted = true;

        const loadProducts = async () => {
            setIsLoading(true);
            try {
                const apiProducts = await fetchProducts();
                if (!isMounted) return;
                setProducts(apiProducts);
                setFilteredProducts(apiProducts);
                setCurrentPage(1);
                setLoadError(null);
            } catch (error) {
                console.error('Error loading products:', error);
                if (!isMounted) return;
                setProducts(fallbackProducts);
                setFilteredProducts(fallbackProducts);
                setCurrentPage(1);
                setLoadError('Produkte nuk u ngarkuan nga serveri. Po shfaqen produktet rezervë.');
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadProducts();

        return () => {
            isMounted = false;
        };
    }, [fallbackProducts]);

    React.useEffect(() => {
        let result = products;

        if (selectedCategories.length > 0) {
            result = result.filter(product => {
                const productCategories = Array.isArray(product.categories) && product.categories.length > 0
                    ? product.categories
                    : product.category
                    ? [product.category]
                    : [];

                return productCategories.some(category => selectedCategories.includes(category));
            });
        }

        if (selectedBrands.length > 0) {
            result = result.filter(product => selectedBrands.includes(product.brand));
        }

        if (parsedMin !== null || parsedMax !== null) {
            result = result.filter((product) => {
                const { min, max } = getPriceBounds(product);
                if (parsedMin !== null && max < parsedMin) {
                    return false;
                }
                if (parsedMax !== null && min > parsedMax) {
                    return false;
                }
                return true;
            });
        }

        setFilteredProducts(result);
        setCurrentPage(1);
    }, [selectedCategories, selectedBrands, parsedMin, parsedMax, products]);

    React.useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
        setCurrentPage((prevPage) => Math.min(prevPage, maxPage));
    }, [filteredProducts]);

    const availableCategories = React.useMemo(() => {
        const categorySet = new Set();
        products.forEach(product => {
            const productCategories = Array.isArray(product.categories) && product.categories.length > 0
                ? product.categories
                : product.category
                ? [product.category]
                : [];
            productCategories.forEach(category => category && categorySet.add(category));
        });
        return Array.from(categorySet).sort();
    }, [products]);

    const availableBrands = React.useMemo(() => {
        const brandSet = new Set();
        products.forEach(product => {
            if (product.brand) {
                brandSet.add(product.brand);
            }
        });
        return Array.from(brandSet).sort();
    }, [products]);

    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
    const pageNumbers = React.useMemo(() => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const dynamicPages = [];
        const firstPage = 1;
        const lastPage = totalPages;
        const windowStart = Math.max(firstPage + 1, currentPage - 1);
        const windowEnd = Math.min(lastPage - 1, currentPage + 1);

        dynamicPages.push(firstPage);
        if (windowStart > firstPage + 1) {
            dynamicPages.push('start-ellipsis');
        }

        for (let page = windowStart; page <= windowEnd; page += 1) {
            dynamicPages.push(page);
        }

        if (windowEnd < lastPage - 1) {
            dynamicPages.push('end-ellipsis');
        }

        dynamicPages.push(lastPage);
        return dynamicPages;
    }, [currentPage, totalPages]);
    const paginatedProducts = React.useMemo(() => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        return filteredProducts.slice(startIndex, startIndex + PAGE_SIZE);
    }, [filteredProducts, currentPage]);

    const startItem = filteredProducts.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, filteredProducts.length);

    const changePage = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <Navbar />
            <SectionBreak />
            <div className="container mx-auto px-4 pt-4">
                <div className="md:hidden flex justify-end mb-4">
                    <button
                        type="button"
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium"
                        onClick={() => setIsFilterOpen(true)}
                        disabled={isLoading}
                    >
                        Filtrat
                    </button>
                </div>
                {isFilterOpen && (
                    <div className="md:hidden mb-6 border border-gray-200 rounded-md p-4 bg-[#F9FAFB]">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold">Filtrat</h2>
                            <button
                                type="button"
                                className="text-sm px-3 py-1 border rounded-md"
                                onClick={() => setIsFilterOpen(false)}
                            >
                                Mbyll
                            </button>
                        </div>
                        <Sidebar 
                            onFilterChange={handleFilterChange} 
                            categories={availableCategories}
                            brands={availableBrands}
                            priceRange={priceRange}
                            onPriceChange={handlePriceChange}
                            disabled={isLoading}
                        />
                    </div>
                )}
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="hidden md:block md:w-1/4 lg:w-1/5">
                        <Sidebar 
                            onFilterChange={handleFilterChange} 
                            categories={availableCategories}
                            brands={availableBrands}
                            priceRange={priceRange}
                            onPriceChange={handlePriceChange}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="w-full md:w-3/4 lg:w-4/5">
                        <div className="mb-4">
                            <h1 className="text-2xl font-bold text-red-600">Dyqani</h1>
                            <div className="text-sm text-gray-500">
                                Home / Products / <span className="font-medium">All Items</span>
                            </div>
                        </div>
                        {loadError && (
                            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                                {loadError}
                            </div>
                        )}
                        {isLoading ? (
                            <div className="py-12 text-center text-gray-500">Duke ngarkuar produktet...</div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="py-12 text-center text-gray-500">Nuk u gjetën produkte me filtrat aktualë.</div>
                        ) : (
                            <>
                                <ProductGrid
                                    products={paginatedProducts}
                                />
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 text-sm text-gray-600">
                                    <div className="text-center sm:text-left">
                                        Duke shfaqur {startItem}-{endItem} nga {filteredProducts.length} produkte
                                    </div>
                                    <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 w-full sm:w-auto">
                                        <button
                                            type="button"
                                            className="px-3 py-2 min-w-[48px] border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => changePage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            aria-label="Faqja e mëparshme"
                                        >
                                            Para
                                        </button>
                                        {pageNumbers.map((token, index) =>
                                            typeof token === 'number' ? (
                                                <button
                                                    type="button"
                                                    key={token}
                                                    className={`px-3 py-2 min-w-[44px] border rounded-md text-sm transition ${
                                                        token === currentPage
                                                            ? 'bg-black text-white border-black'
                                                            : 'bg-white text-gray-700'
                                                    }`}
                                                    onClick={() => changePage(token)}
                                                    aria-current={token === currentPage ? 'page' : undefined}
                                                >
                                                    {token}
                                                </button>
                                            ) : (
                                                <span
                                                    key={`${token}-${index}`}
                                                    className="px-2 text-gray-400"
                                                    aria-hidden="true"
                                                >
                                                    ...
                                                </span>
                                            )
                                        )}
                                        <button
                                            type="button"
                                            className="px-3 py-2 min-w-[48px] border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => changePage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            aria-label="Faqja tjetër"
                                        >
                                            Pas
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <SectionBreak />
            <Footer />
        </div>
    );
};

export default Index;
